import { ArrowRight, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { usePublishDesign } from '@/hooks/usePublishDesign';
import { cn } from '@/lib/utils';

interface PublishActionButtonProps {
  /**
   * Show the impersonation handover notice above the button. Only the
   * button in the step body sets it; the compact one in the header does not.
   */
  withImpersonationNotice?: boolean;
  /**
   * The header's form: the height of "Weiter", one line, and a long channel
   * name cut off (the full label stays in the tooltip). The step body keeps
   * the large button.
   */
  inHeader?: boolean;
}

/**
 * Primary CTA for Step 3 — aligned bottom-right per publish mock.
 */
export function PublishActionButton({
  withImpersonationNotice = false,
  inHeader = false,
}: PublishActionButtonProps) {
  const { t } = useTranslation();
  const {
    publish,
    isPublishing,
    canPublish,
    publishBlockedReason,
    impersonationHandover,
    targetChannels,
    availableChannels,
    isCloudSession,
  } = usePublishDesign();

  /**
   * The button names its target (plan U9). One channel gets its name, several
   * get their count; a cloud session and a seller without channels keep the
   * neutral label because neither picks a destination here.
   */
  const label =
    isCloudSession || availableChannels.length === 0
      ? t('creator.publish.createProducts')
      : targetChannels.length === 1 && targetChannels[0]
        ? t('creator.publish.publishToChannel', {
            channel: targetChannels[0].displayName,
          })
        : t('creator.publish.publishToChannels', {
            count: targetChannels.length,
          });

  const button = (
    <div className="flex justify-end">
      <button
        type="button"
        onClick={publish}
        disabled={!canPublish || isPublishing}
        aria-disabled={!canPublish || isPublishing}
        title={
          canPublish
            ? label
            : (publishBlockedReason ?? t('creator.publish.blockedMissingInput'))
        }
        data-size="lg"
        className={cn(
          'dc-button inline-flex items-center gap-2 rounded-control text-[15px] font-display tracking-[-0.01em] transition',
          inHeader
            ? 'h-[42px] max-w-[300px] whitespace-nowrap px-[18px] font-semibold'
            : 'px-7 py-3.5 font-bold',
          canPublish && !isPublishing
            ? 'bg-primary text-primary-fg hover:brightness-[0.92]'
            : 'bg-[var(--paper-3)] text-[var(--mute)] cursor-not-allowed'
        )}
      >
        {isPublishing ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            {t('creator.publish.creating')}
          </>
        ) : (
          <>
            <span className="min-w-0 truncate">{label}</span>
            <ArrowRight className="w-4 h-4 flex-none" />
          </>
        )}
      </button>
    </div>
  );

  if (!withImpersonationNotice || impersonationHandover === null) {
    return button;
  }

  // Said before the click: an admin acting for the seller must know whether
  // this publish reaches the shop at all.
  const sendsNothing = impersonationHandover === 'none';
  return (
    <div className="flex flex-col gap-3">
      <p
        role="note"
        className={cn(
          'rounded-control border px-3 py-2 text-xs',
          sendsNothing
            ? 'border-warning bg-[color-mix(in_srgb,var(--warning)_10%,transparent)] text-warning'
            : 'border-[var(--line)] text-[var(--mute)]'
        )}
      >
        {t(
          sendsNothing
            ? 'creator.publish.impersonationNoHandoverNotice'
            : 'creator.publish.impersonationHandoverNotice'
        )}
      </p>
      {button}
    </div>
  );
}
